import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiX, FiClock } from "react-icons/fi";
import moment from "moment-timezone";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import {
  createExtensionRequest,
  CreateExtensionRequestResponse,
  getPendingExtensionRequest,
  PendingExtensionRequest,
  getUpgradePricing,
  UpgradePricingResponse
} from "../../../actions/stay-actions";
import StripeCheckout from "../../../components/Stripe/StripeCheckout";
import confetti from "canvas-confetti";
import StayExtensionCalendar from "./components/StayExtensionCalendar";
import "./Stay.scss";

// Eastern Time zone for Florida properties
const TIMEZONE = "America/New_York";

// Hero images
const EARLY_CHECKIN_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80";
const LATE_CHECKOUT_IMAGE = "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=80";
const EXTEND_STAY_IMAGE = "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&q=80";

// Default hour options (fallback when no PMS pricing is available)
const defaultHourOptions = [
  { hours: 1, label: "1 Hour", price: 25 },
  { hours: 2, label: "2 Hours", price: 45 },
  { hours: 3, label: "3 Hours", price: 65 },
];

// Default extension price ($100/night)
const DEFAULT_PRICE_PER_NIGHT_CENTS = 10000;

// Hour option type for dynamic pricing
interface HourOption {
  hours: number;
  label: string;
  price: number;
}

function Stay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext<AuthContextType>(AuthContext);

  // Determine mode from URL params: 'early', 'late', or 'extend'
  const mode = searchParams.get('mode') || 'extend';

  const [submitting, setSubmitting] = useState(false);
  const [extensionResult, setExtensionResult] = useState<CreateExtensionRequestResponse | null>(null);
  const [extensionError, setExtensionError] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<PendingExtensionRequest | null>(null);
  const [loadingPending, setLoadingPending] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentStep, setShowPaymentStep] = useState(false);

  // Dynamic pricing state
  const [pricing, setPricing] = useState<UpgradePricingResponse | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // For hour-based selection (early check-in / late checkout)
  const [selectedHourOption, setSelectedHourOption] = useState<HourOption | null>(null);

  // For calendar-based selection (stay extension)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedNights, setSelectedNights] = useState<number>(0);

  // Get hour options based on mode and pricing
  const getHourOptions = (): HourOption[] => {
    if (!pricing) return defaultHourOptions;

    const prices = mode === 'early' ? pricing.earlyCheckinPrices : pricing.lateCheckoutPrices;
    if (!prices || prices.length === 0) return defaultHourOptions;

    return prices.map(p => ({
      hours: p.hours,
      label: p.label,
      price: p.priceCents / 100 // Convert cents to dollars for display
    }));
  };

  // Get price per night in cents for extension
  const pricePerNightCents = pricing?.extensionPricePerNightCents ?? DEFAULT_PRICE_PER_NIGHT_CENTS;

  // Get currency from pricing or default to USD
  const currency = pricing?.currency ?? "USD";

  // Format date with time in Eastern Time
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return moment.utc(dateString).tz(TIMEZONE).format("ddd, MMM D [at] h:mm A");
  };

  // Get the estimated price based on selection
  const getEstimatedPrice = () => {
    if (mode === 'extend') {
      return selectedNights * pricePerNightCents / 100;
    }
    return selectedHourOption?.price || 0;
  };

  // Get hero image based on mode
  const getHeroImage = () => {
    switch (mode) {
      case 'early':
        return user?.unitPhotoUrl || EARLY_CHECKIN_IMAGE;
      case 'late':
        return user?.unitPhotoUrl || LATE_CHECKOUT_IMAGE;
      default:
        return user?.unitPhotoUrl || EXTEND_STAY_IMAGE;
    }
  };

  // Get title based on mode
  const getTitle = () => {
    switch (mode) {
      case 'early':
        return "Request Early Check-in";
      case 'late':
        return "Request Late Checkout";
      default:
        return "Extend Your Stay";
    }
  };

  // Get description based on mode - shortened for better UX
  const getDescription = () => {
    switch (mode) {
      case 'early':
        return "Arrive early and get a head start on your vacation.";
      case 'late':
        return "Enjoy your last morning at a relaxed pace.";
      default:
        return "Select your new checkout date below.";
    }
  };

  // Get selection label based on mode
  const getSelectionLabel = () => {
    if (mode === 'extend') {
      return selectedNights > 0
        ? `${selectedNights} extra night${selectedNights !== 1 ? 's' : ''}`
        : "None selected";
    }
    return selectedHourOption
      ? `${selectedHourOption.label} Early Access`
      : "None selected";
  };

  // Fetch pending extension request and pricing on mount
  useEffect(() => {
    const fetchPendingRequest = async () => {
      setLoadingPending(true);
      try {
        const response = await getPendingExtensionRequest();
        setPendingRequest(response.data);
      } catch (err) {
        console.error("Failed to fetch pending request:", err);
      } finally {
        setLoadingPending(false);
      }
    };

    const fetchPricing = async () => {
      setLoadingPricing(true);
      try {
        const response = await getUpgradePricing();
        setPricing(response.data);
      } catch (err) {
        console.error("Failed to fetch pricing:", err);
        // Continue with defaults if pricing fetch fails
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPendingRequest();
    fetchPricing();
  }, []);

  const handleHourOptionSelect = (option: HourOption) => {
    setSelectedHourOption(option);
  };

  const handleDateSelected = (date: Date, nightsCount: number) => {
    setSelectedDate(date);
    setSelectedNights(nightsCount);
  };

  const handleContinue = () => {
    if (mode === 'extend' && !selectedDate) return;
    if ((mode === 'early' || mode === 'late') && !selectedHourOption) return;
    setShowPaymentModal(true);
    setExtensionError(null);
    setShowPaymentStep(false);
  };

  const handleProceedToPayment = async () => {
    setSubmitting(true);
    setExtensionError(null);

    try {
      let requestedDepartureTimestamp: number;

      if (mode === 'extend' && selectedDate) {
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);
        requestedDepartureTimestamp = endOfDay.getTime();
      } else if (mode === 'early' && selectedHourOption && user?.checkIn) {
        // For early check-in, subtract hours from check-in time
        const checkInTime = new Date(user.checkIn);
        requestedDepartureTimestamp = checkInTime.getTime() - (selectedHourOption.hours * 60 * 60 * 1000);
      } else if (mode === 'late' && selectedHourOption && user?.checkOut) {
        // For late checkout, add hours to checkout time
        const checkOutTime = new Date(user.checkOut);
        requestedDepartureTimestamp = checkOutTime.getTime() + (selectedHourOption.hours * 60 * 60 * 1000);
      } else {
        throw new Error("Invalid selection");
      }

      const response = await createExtensionRequest({
        requestedDepartureTimestamp
      });

      setExtensionResult(response.data);
      setShowPaymentStep(true);
    } catch (err: any) {
      console.error("Failed to create extension request:", err);
      setExtensionError(err.response?.data || "Failed to create request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const triggerConfetti = () => {
    const colors = ["#ffffff", "#22c55e", "#3b82f6", "#f59e0b", "#ef4444"];
    const end = Date.now() + 1000;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setShowPaymentStep(false);

    triggerConfetti();

    try {
      const response = await getPendingExtensionRequest();
      setPendingRequest(response.data);
    } catch (err) {
      // Silent fail
    }

    setSelectedDate(null);
    setSelectedNights(0);
    setSelectedHourOption(null);
    setExtensionResult(null);
  };

  const handlePaymentError = (error: string) => {
    setExtensionError(error);
  };

  const handleCloseModal = () => {
    setShowPaymentModal(false);
    setShowPaymentStep(false);
    setExtensionError(null);
    setExtensionResult(null);
  };

  const handleClose = () => {
    navigate("/admin/upgrades");
  };

  const estimatedPrice = getEstimatedPrice();
  const hasSelection = mode === 'extend' ? selectedDate !== null : selectedHourOption !== null;

  // Loading state - wait for both pending request and pricing
  if (loadingPending || loadingPricing) {
    return (
      <div className="stay-loading dark-theme">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Get the hour options for rendering
  const hourOptions = getHourOptions();

  return (
    <div className={`stay-page dark-theme ${pendingRequest ? 'stay-page--pending' : ''}`}>
      {/* Close Button */}
      <button className="stay-page__close" onClick={handleClose}>
        <FiX size={22} />
      </button>

      {/* Hero Section */}
      <div className="stay-hero">
        <div
          className="stay-hero__image"
          style={{ backgroundImage: `url(${getHeroImage()})` }}
        />
        <div className="stay-hero__overlay" />
        <div className="stay-hero__content">
          <h1 className="stay-hero__title">{getTitle()}</h1>
          <p className="stay-hero__description">{getDescription()}</p>
        </div>
      </div>

      {/* Content */}
      <div className="stay-content">
        {/* Hour Options for Early Check-in / Late Checkout */}
        {(mode === 'early' || mode === 'late') && (
          <div className="hour-options">
            {hourOptions.map((option) => (
              <button
                key={option.hours}
                className={`hour-option ${selectedHourOption?.hours === option.hours ? 'hour-option--selected' : ''}`}
                onClick={() => handleHourOptionSelect(option)}
                disabled={!!pendingRequest}
              >
                <div className="hour-option__radio">
                  {selectedHourOption?.hours === option.hours && (
                    <div className="hour-option__radio-dot" />
                  )}
                </div>
                <span className="hour-option__label">
                  {option.label} {mode === 'early' ? 'Early Access' : 'Late Checkout'}
                </span>
                <span className="hour-option__price">${option.price} {currency}</span>
              </button>
            ))}
          </div>
        )}

        {/* Calendar for Stay Extension */}
        {mode === 'extend' && (
          <div className="stay-calendar">
            <StayExtensionCalendar
              checkOut={user?.checkOut || ""}
              onSelectDate={handleDateSelected}
              hasPendingRequest={!!pendingRequest}
            />
          </div>
        )}

      </div>

      {/* Sticky Action Bar */}
      <div className={`stay-action-bar ${pendingRequest ? 'stay-action-bar--pending' : ''}`}>
        <div className="stay-action-bar__container">
          {pendingRequest ? (
            <div className="stay-action-bar__pending">
              <div className="stay-action-bar__pending-icon">
                <FiClock size={20} />
              </div>
              <div className="stay-action-bar__pending-content">
                <h4 className="stay-action-bar__pending-title">Request Pending</h4>
                <p className="stay-action-bar__pending-text">
                  Awaiting approval from property manager
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="stay-action-bar__header">
                <span className="stay-action-bar__label">Your upgrade:</span>
                {hasSelection && (
                  <span className="stay-action-bar__price">${estimatedPrice.toFixed(2)} {currency}</span>
                )}
              </div>
              <div className="stay-action-bar__selection">
                {getSelectionLabel()}
              </div>
              <button
                className={`stay-action-bar__button ${!hasSelection ? 'stay-action-bar__button--disabled' : ''}`}
                onClick={handleContinue}
                disabled={!hasSelection}
              >
                {hasSelection ? "Purchase" : "Select your option"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="payment-modal">
          <div className="payment-modal__backdrop" onClick={handleCloseModal} />
          <div className="payment-modal__content">
            <div className="payment-modal__header">
              <h3>{showPaymentStep ? "Enter Payment Details" : "Confirm Your Selection"}</h3>
              <button className="payment-modal__close" onClick={handleCloseModal}>
                <FiX size={18} />
              </button>
            </div>

            {extensionError && (
              <div className="payment-modal__error">
                <p>{extensionError}</p>
              </div>
            )}

            {!showPaymentStep ? (
              <>
                <div className="payment-modal__summary">
                  <div className="payment-modal__row">
                    <span className="payment-modal__label">
                      {mode === 'early' ? 'Current check-in' : mode === 'late' ? 'Current checkout' : 'Current checkout'}
                    </span>
                    <span className="payment-modal__value">
                      {mode === 'early' ? formatDateTime(user?.checkIn) : formatDateTime(user?.checkOut)}
                    </span>
                  </div>
                  <div className="payment-modal__row">
                    <span className="payment-modal__label">Selection</span>
                    <span className="payment-modal__value">{getSelectionLabel()}</span>
                  </div>
                  <div className="payment-modal__row" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-card)' }}>
                    <span className="payment-modal__label">Total</span>
                    <span className="payment-modal__value payment-modal__value--highlight">
                      ${estimatedPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                <p className="payment-modal__note">
                  Your card will be authorized for this amount. The charge will only be captured if the property manager approves your request.
                </p>

                <div className="payment-modal__actions">
                  <button
                    className="payment-modal__btn payment-modal__btn--secondary"
                    onClick={handleCloseModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="payment-modal__btn payment-modal__btn--primary"
                    onClick={handleProceedToPayment}
                    disabled={submitting}
                  >
                    {submitting ? "Processing..." : "Continue to Payment"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="payment-modal__summary">
                  <div className="payment-modal__row">
                    <span className="payment-modal__label">Selection</span>
                    <span className="payment-modal__value">{getSelectionLabel()}</span>
                  </div>
                  <div className="payment-modal__row">
                    <span className="payment-modal__label">Amount to authorize</span>
                    <span className="payment-modal__value payment-modal__value--highlight">
                      ${estimatedPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                {extensionResult?.clientSecret ? (
                  <StripeCheckout
                    clientSecret={extensionResult.clientSecret}
                    amount={estimatedPrice}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Preparing payment...</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Stay;
